import { PlusOutlined } from "@ant-design/icons"
import { Alert, Button, Flex, Skeleton, Typography } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams, useSearchParams } from "react-router-dom"

import { useAppDispatch } from "../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import * as api from "./api/services.ts"
import { ConversationView } from "./components/ConversationView.tsx"
import { NewConversationModal } from "./components/NewConversationModal.tsx"
import { ProjectConversationList } from "./components/ProjectConversationList.tsx"
import { useOpenConversations } from "./hooks/use-open-conversations.ts"
import { upsertConversation } from "./redux/messaging.slice.ts"
import type { Participant, ProjectConversations } from "./types/messaging.types.ts"

/**
 * A project's Chat tab — the only entry point to messaging in this phase.
 *
 * Desktop: a compact room list beside the open conversation. Under `lg` the
 * two become separate screens, list first, with a back control in the header.
 */
export function ProjectChatSection() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { id: projectId } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  const [rooms, setRooms] = useState<ProjectConversations | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [newOpen, setNewOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const conversations = useAppSelector((state) => state.messaging.conversations)
  const selectedId = searchParams.get("conversation")
  const focusMessageId = searchParams.get("message")

  const load = async (selectFirst = true) => {
    if (!projectId) return
    setLoading(true)
    setError(false)
    const response = await api.getProjectConversations(projectId)
    setLoading(false)
    if (!response.ok || !response.data) {
      setError(true)
      return
    }
    setRooms(response.data)
    const found = api.roomsOf(response.data)
    found.forEach((room) => dispatch(upsertConversation(room)))
    // Only on a screen wide enough to show both panels. On a phone the two
    // are separate screens, and auto-selecting would skip the list entirely.
    const twoPanel =
      typeof window !== "undefined" &&
      window.matchMedia?.("(min-width: 1024px)").matches
    if (selectFirst && twoPanel && !selectedId && found[0]) {
      select(found[0].id, { replace: true })
    }
  }

  useEffect(() => {
    load()
  }, [projectId])

  const select = (
    conversationId: string,
    { replace = false }: { replace?: boolean } = {}
  ) => {
    const next = new URLSearchParams(searchParams)
    next.set("conversation", conversationId)
    next.delete("message")
    setSearchParams(next, { replace })
  }

  const roomIds = useMemo(
    () =>
      [
        rooms?.team_chat?.id,
        rooms?.customer_chat?.id,
        ...(rooms?.groups ?? []).map((room) => room.id),
        ...(rooms?.direct_chats ?? []).map((room) => room.id)
      ].filter(Boolean) as string[],
    [rooms]
  )

  useOpenConversations(roomIds)

  const active = useMemo(
    () => conversations.find((row) => row.id === selectedId) ?? null,
    [conversations, selectedId]
  )

  // The store is the source for a row once loaded, so unread counts and the
  // last-message line stay live without refetching the tab.
  const fromStore = (id: string | undefined) =>
    id ? (conversations.find((row) => row.id === id) ?? null) : null

  if (loading && !rooms) return <Skeleton active paragraph={{ rows: 8 }} />

  if (error) {
    return (
      <Alert
        type={"error"}
        showIcon
        message={t("messages.errors.chatTabTitle", "Unable to load chat")}
        description={t(
          "messages.errors.chatTabBody",
          "Something went wrong while opening this project's conversations. Please try again."
        )}
        action={<Button onClick={() => load()}>{t("common.retry", "Retry")}</Button>}
      />
    )
  }

  return (
    // One centred column for the heading, the action and the workspace, so the
    // three line up with each other rather than each spanning the monitor. The
    // project header and its tab bar above are untouched and stay full width.
    //
    // Height is a cap, not a fixed size: `min()` takes the 680px default on a
    // tall screen and the space actually available on a short one, so the
    // composer stays on screen either way. Expanding lifts the width cap and
    // lets the workspace take the viewport height.
    <div
      data-testid={"messages-shell"}
      className={`mx-auto flex min-h-0 w-full flex-col gap-3 ${
        expanded ? "max-w-none" : "max-w-[1280px]"
      }`}
      // Inline rather than an arbitrary utility: `min()` wrapping a `calc()`
      // that reads a custom property is more than the class scanner will
      // reliably emit, and a height that silently fails to apply is exactly
      // the bug being fixed. 680px is the default cap; the viewport term wins
      // on a short screen, which is what keeps the composer on it.
      style={{
        height: expanded
          ? "calc(100vh - var(--header-height) - 11rem)"
          : "min(42.5rem, calc(100vh - var(--header-height) - 19rem))",
        minHeight: "24rem"
      }}
    >
      {rooms?.missing_branch && (
        <Alert
          type={"warning"}
          showIcon
          message={t(
            "messages.missingBranch.title",
            "This project is not assigned to a branch"
          )}
          description={t(
            "messages.missingBranch.body",
            "A conversation belongs to a branch, so this project needs one before its chat can be opened. Assign it to a branch in its settings."
          )}
        />
      )}

      {rooms && !rooms.customer_setup.ready && (
        <Alert
          type={"info"}
          showIcon
          message={t("messages.customerSetup.title", "Customer chat is not ready yet")}
          description={
            <ul className={"m-0 list-disc pl-4"}>
              {rooms.customer_setup.missing_owner && (
                <li>
                  {t(
                    "messages.customerSetup.noPm",
                    "Assign a project manager to this project."
                  )}
                </li>
              )}
              {rooms.customer_setup.missing_customer_account && (
                <li>
                  {t(
                    "messages.customerSetup.noCustomer",
                    "The customer has not activated their portal account yet."
                  )}
                </li>
              )}
            </ul>
          }
        />
      )}

      {rooms?.archived && (
        <Alert
          type={"warning"}
          showIcon
          message={t(
            "messages.archivedProject",
            "This project is archived. Chat history stays readable and nothing new can be sent."
          )}
        />
      )}

      {/* Content header: heading, muted subtitle, right-aligned primary action.
          The project name and address stay in the page header above — repeating
          them here would give the screen two titles. */}
      <Flex align={"flex-start"} justify={"space-between"} gap={"small"}>
        <div>
          <Typography.Title level={4} className={"!mb-0"}>
            {t("messages.title", "Messages")}
          </Typography.Title>
          <Typography.Text type={"secondary"} className={"text-[13px]"}>
            {t("messages.subtitle", "Conversations for this project.")}
          </Typography.Text>
        </div>
        {!rooms?.archived && (
          <Button
            type={"primary"}
            icon={<PlusOutlined />}
            onClick={() => setNewOpen(true)}
          >
            {t("messages.newConversation", "New conversation")}
          </Button>
        )}
      </Flex>

      <div
        data-testid={"project-chat"}
        className={
          "flex min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-white"
        }
      >
        <div
          className={`${
            active ? "hidden lg:flex" : "flex"
          } w-full shrink-0 flex-col border-r border-slate-200 lg:w-[300px]`}
        >
          <ProjectConversationList
            teamChat={fromStore(rooms?.team_chat?.id) ?? rooms?.team_chat ?? null}
            customerChat={
              fromStore(rooms?.customer_chat?.id) ?? rooms?.customer_chat ?? null
            }
            groups={(rooms?.groups ?? []).map(
              (room) => fromStore(room.id) ?? room
            )}
            directs={(rooms?.direct_chats ?? []).map(
              (room) => fromStore(room.id) ?? room
            )}
            activeId={selectedId}
            canCreate={!rooms?.archived}
            onSelect={(conversation) => select(conversation.id)}
            onNewConversation={() => setNewOpen(true)}
          />
        </div>

        <div className={`${active ? "flex" : "hidden lg:flex"} min-w-0 flex-1 flex-col`}>
          {active ? (
            <ConversationView
              key={active.id}
              conversation={active}
              showBack
              focusMessageId={focusMessageId}
              expanded={expanded}
              onToggleExpand={() => setExpanded((on) => !on)}
              onBack={() => {
                const next = new URLSearchParams(searchParams)
                next.delete("conversation")
                next.delete("message")
                setSearchParams(next, { replace: true })
              }}
            />
          ) : (
            <Typography.Paragraph className={"m-auto max-w-xs text-center"} type={"secondary"}>
              {t(
                "messages.pickRoom",
                "Pick a conversation to read it here."
              )}
            </Typography.Paragraph>
          )}
        </div>
      </div>

      {rooms && (
        <NewConversationModal
          open={newOpen}
          projectId={rooms.project_id}
          projectName={rooms.project_name}
          canCreateGroup={rooms.can_manage_members}
          onClose={() => setNewOpen(false)}
          onCreateDirect={async (person: Participant) => {
            const response = await api.openDirectConversation(person.id, rooms.project_id)
            setNewOpen(false)
            if (!response.ok || !response.data) return
            dispatch(upsertConversation(response.data))
            await load(false)
            select(response.data.id)
          }}
          onCreateGroup={async (name, memberIds) => {
            const response = await api.createContributorGroup(rooms.project_id, {
              name,
              member_ids: memberIds
            })
            setNewOpen(false)
            if (!response.ok || !response.data) return
            dispatch(upsertConversation(response.data))
            await load(false)
            select(response.data.id)
          }}
        />
      )}
    </div>
  )
}

export default ProjectChatSection
